import {
  Button,
  FormHelperText,
  IconButton,
  Input,
  InputLabel,
  ListItem,
  ListItemSecondaryAction,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { red } from '@mui/material/colors';
import EditIcon from '@mui/icons-material/Edit';
import Skeleton from '@mui/material/Skeleton';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { ICategory } from '../../../../shared-interfaces/Notation/ICategory';
import CategoryService from '../Services/CategoryService';

interface ICategoryListItem {
  category: ICategory;
  canEnable: boolean;
  onUpdate: (c: ICategory) => void;
}

const useStyles = makeStyles({
  ListItem: {
    minHeight: '80px',
    borderBottom: '1px solid silver',
    '&:hover': {
      '& $Edit ': {
        display: 'inline',
      },
    },
    '&:focus': {
      '& $Edit ': {
        display: 'inline',
      },
    },
  },
  Edit: {
    display: 'none',
  },
  EditName: {
    minWidth: '250px',
    maxWidth: '450px',
  },
  error: {
    color: red[800],
    fontSize: '12px',
  },
  required: {
    color: red[800],
    fontSize: '12px',
  },
});

export const CategoryListItemLoader = () => {
  const classes = useStyles();
  return (
    <ListItem className={classes.ListItem}>
      <Skeleton variant="text" width={250} height={30} />
      <ListItemSecondaryAction>
        <Skeleton variant="text" width={60} height={40} />
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const CategoryListItem = (props: ICategoryListItem) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [isEditable, setIsEditable] = useState(false);
  const [category, setCategory] = useState(props.category);
  const [categoryName, setCategoryName] = useState(props.category.name);
  const [isActive, setIsActive] = useState(props.category.isActive);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function disableHoverListener(): boolean | undefined {
    if (!props.canEnable) {
      return isActive;
    }
    return true;
  }

  const handleSave = () => {
    setIsSubmitting(true);
    CategoryService.updateCategory({
      id: category.id,
      name: categoryName,
      isActive: category.isActive,
    } as ICategory)
      .then(c => {
        setCategory(c);
        props.onUpdate(c);
        enqueueSnackbar('Success!', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed', { variant: 'error' });
        setCategoryName(props.category.name);
      })
      .finally(() => {
        setIsEditable(false);
        setIsSubmitting(false);
      });
  };

  const handleToggle = () => {
    setIsActive(!isActive);
    setCategory(c => {
      c.isActive === !isActive;
      return c;
    });
    CategoryService.updateCategory({
      id: category.id,
      name: categoryName,
      isActive: !isActive,
    } as ICategory)
      .then(c => {
        props.onUpdate(c);
        enqueueSnackbar('Success!', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed', { variant: 'error' });
        setIsActive(props.category.isActive);
      })
      .finally(() => {
        setIsEditable(false);
        setIsSubmitting(false);
      });
  };

  const handleCancel = () => {
    setCategoryName(props.category.name);
    setIsEditable(false);
  };

  return (
    <ListItem className={classes.ListItem} button disableRipple>
      {isEditable ? (
        <>
          <div className={classes.EditName}>
            <InputLabel
              htmlFor={`category ${category.name}`}
              className="sr-only"
            >
              <span className={classes.required}>*</span> Category Name
            </InputLabel>
            <Input
              value={categoryName}
              id={category.name}
              onChange={e => setCategoryName(e.target.value)}
              fullWidth
              required
              error={!categoryName || categoryName.length > 30}
            />
            {(!categoryName || categoryName.length > 30) && (
              <FormHelperText className={classes.error}>
                Enter a category name of 30 characters or less
              </FormHelperText>
            )}
          </div>
          <div style={{ marginLeft: '20px' }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={
                !categoryName || isSubmitting || categoryName.length > 30
              }
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <Typography>{categoryName}</Typography>
          <IconButton
            className={classes.Edit}
            color="primary"
            size="small"
            onClick={() => setIsEditable(true)}
          >
            <EditIcon />
            <span className="sr-only">Name</span>
          </IconButton>
        </>
      )}

      <ListItemSecondaryAction>
        <Tooltip
          title="30 active category limit reached"
          disableHoverListener={disableHoverListener()}
        >
          <span>
            <Switch
              color="primary"
              edge="end"
              checked={isActive}
              inputProps={{ 'aria-label': 'active' }}
              disabled={!isActive && !props.canEnable}
              onChange={handleToggle}
            />
          </span>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default CategoryListItem;
